import React, { useState, useEffect, useMemo } from 'react'
import { SearchBar, LoadingSpinner, MarkdownRenderer } from '../utils'
import { useFaq } from '../../hooks/useFaq'

// Função para adicionar keyframes de animação
const addBlinkAnimation = () => {
  const style = document.createElement('style')
  style.textContent = `
    @keyframes blinkHighlight {
      0% { background-color: transparent; }
      50% { background-color: rgba(0, 114, 187, 0.1); }
      100% { background-color: transparent; }
    }
  `
  document.head.appendChild(style)
}

// Função para detectar URLs e convertê-las para markdown
const convertUrlsToMarkdown = (text: string): string => {
  if (!text) return text
  
  // Regex para detectar URLs (http, https, www)
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g
  
  return text.replace(urlRegex, (url) => {
    // Adiciona https:// se a URL começa com www
    const fullUrl = url.startsWith('www.') ? `https://${url}` : url
    return `[${url}](${fullUrl})`
  })
}

export function DuvidasPage() {
  const { data, loading, error, refetch } = useFaq()
  const [searchTerm, setSearchTerm] = useState('')
  const [isMobile, setIsMobile] = useState(false)
  const [isSmallMobile, setIsSmallMobile] = useState(false)
  const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>({})

  // Adicionar animação de piscar
  useEffect(() => {
    addBlinkAnimation()
  }, [])

  // Hook para detectar tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
      setIsSmallMobile(window.innerWidth <= 480)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Filtra os cards baseado no termo de pesquisa
  const filteredCards = useMemo(() => {
    if (!data?.FAQCards) return []
    
    if (!searchTerm.trim()) return data.FAQCards

    return data.FAQCards.filter((card: any) => {
      const cardTitle = card.title?.toLowerCase() || ''
      const searchLower = searchTerm.toLowerCase()
      
      // Verifica se o título do card contém o termo de pesquisa
      if (cardTitle.includes(searchLower)) return true
      
      // Verifica se alguma dúvida dentro do card contém o termo
      const duvidas = card.DuvidasFrequentes || []
      return duvidas.some((duvida: any) => {
        const duvidaTitle = duvida.titleDuvida?.toLowerCase() || ''
        return duvidaTitle.includes(searchLower)
      })
    })
  }, [data, searchTerm])

  const clearSearch = () => {
    setSearchTerm('')
  }

  const hasActiveFilters = searchTerm.trim().length > 0

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }))
  }

  if (loading) {
    return <LoadingSpinner message="Carregando dúvidas frequentes..." />
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center',
        background: '#EFF4F6',
        color: '#333'
      }}>
        <h2>Erro ao carregar dúvidas frequentes</h2>
        <p>{error}</p>
        <button
          onClick={refetch}
          style={{
            padding: '10px 20px',
            backgroundColor: '#0072BB',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginTop: '10px'
          }}
        >
          Tentar Novamente
        </button>
      </div>
    )
  }

  return (
    <div style={{ 
      background: '#EFF4F6',
      color: '#333',
      fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      letterSpacing: 0.3
    }}>
      {/* Primeira Seção - Background Azul (apenas desktop) */}
      {!isMobile && (
        <div style={{
          background: '#0072BB',
          paddingTop: '20px',
          paddingBottom: '2rem'
        }}>
          <div style={{
            margin: '0 auto',
            padding: '20px 0px',
            paddingTop: '20px',
            width: '80%',
            boxSizing: 'border-box'
          }}>
            {/* Container para Busca e Título */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '3rem',
              alignItems: 'flex-start',
              marginBottom: '2rem'
            }}>
              {/* Campo de Busca */}
              <SearchBar
                placeholder="Pesquisar tópicos de ajuda..."
                value={searchTerm}
                onChange={setSearchTerm}
                onClear={clearSearch}
                showResultsCount={hasActiveFilters}
                resultsCount={filteredCards.length}
                resultsLabel={filteredCards.length === 1 ? 'tópico encontrado' : 'tópicos encontrados'}
                style={{
                  marginBottom: '1rem',
                  margin: '0',
                  maxWidth: '400px',
                  width: '100%'
                }}
              />

              {/* Título Principal */}
              <h1 style={{
                fontSize: '2.2rem',
                fontWeight: '700',
                textAlign: 'left',
                color: 'white',
                margin: '0',
                fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
              }}>
                Dúvidas Frequentes
              </h1>
            </div>
            
            {/* Grid de cards de FAQ */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
              gap: '1rem',
              margin: '0 auto',
              marginTop: '1rem',
              width: '100%'
            }}>
              {filteredCards.length > 0 ? (
                filteredCards.map((card: any) => (
                  <div
                    key={card.id}
                    style={{
                      background: 'white',
                      borderRadius: '8px',
                      padding: '1rem',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      minHeight: '80px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center'
                    }}
                    onClick={() => {
                      const sectionElement = document.getElementById(`faq-section-${card.id}`)
                      if (sectionElement) {
                        sectionElement.scrollIntoView({ 
                          behavior: 'smooth',
                          block: 'center'
                        })
                        
                        // Adicionar animação de piscar após o scroll
                        setTimeout(() => {
                          sectionElement.style.animation = 'blinkHighlight 2s ease-in-out'
                          setTimeout(() => {
                            sectionElement.style.animation = ''
                          }, 2000)
                        }, 500)
                      }
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)'
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.2)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                  >
                    <h3 style={{
                      fontSize: isSmallMobile ? '1rem' : isMobile ? '1.1rem' : '1.2rem',
                      fontWeight: '600',
                      color: '#0072BB',
                      margin: 0,
                      lineHeight: 1.4,
                      fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                    }}>
                      {card.title}
                    </h3>
                  </div>
                ))
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: isSmallMobile ? '1.5rem 1rem' : isMobile ? '2rem 1rem' : '3rem 1rem',
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: isSmallMobile ? '0.9rem' : isMobile ? '1rem' : '1.1rem',
                  gridColumn: '1 / -1'
                }}>
                  {hasActiveFilters ? (
                    <>
                      <p>Nenhum tópico encontrado para "{searchTerm}"</p>
                      <button
                        onClick={clearSearch}
                        style={{
                          marginTop: '1rem',
                          padding: isSmallMobile ? '8px 16px' : '10px 20px',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          border: '2px solid rgba(255, 255, 255, 0.3)',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: isSmallMobile ? '0.9rem' : '1rem'
                        }}
                      >
                        Limpar busca
                      </button>
                    </>
                  ) : (
                    <p>Nenhum tópico disponível</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Segunda Seção - FAQ Dropdown */}
      <div style={{
        padding: isMobile ? '2rem 20px' : '3rem 20px',
        paddingTop: isMobile ? '80px' : '3rem',
        maxWidth: 1520,
        margin: '0 auto'
      }}>
        {data?.FAQCards?.map((section: any) => (
          <div key={section.id} id={`faq-section-${section.id}`} style={{ marginBottom: '3rem' }}>
            {/* Título da Seção */}
            <h2 style={{
              fontSize: '1.8rem',
              fontWeight: '700',
              color: '#0072BB',
              textAlign: 'left',
              marginBottom: '0.5rem',
              marginTop: 0,
              fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
            }}>
              {section.title}
            </h2>

            {/* Lista de Perguntas */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {section.DuvidasFrequentes?.map((duvida: any) => {
                const itemId = `${section.id}-${duvida.id}`
                const isExpanded = expandedItems[itemId]

                return (
                  <div key={duvida.id} style={{ marginBottom: '0.5rem' }}>
                    {/* Header da Pergunta */}
                    <div
                      onClick={() => toggleExpanded(itemId)}
                      style={{
                        background: '#86939C',
                        padding: '1rem 1.5rem',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        textAlign: 'left',
                        borderRadius: '8px 8px 0 0',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#7A8791'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#86939C'
                      }}
                    >
                      <h3 style={{
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        color: 'white',
                        margin: 0,
                        flex: 1,
                        textAlign: 'left',
                        fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                      }}>
                        {duvida.titleDuvida}
                      </h3>
                      <span style={{
                        fontSize: '1.2rem',
                        color: 'white',
                        transition: 'transform 0.3s ease',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                      }}>
                        ▼
                      </span>
                    </div>

                    {/* Conteúdo da Resposta */}
                    {isExpanded && (
                      <div style={{
                        background: 'white',
                        padding: '1.5rem',
                        borderRadius: '0 0 8px 8px',
                        border: '1px solid #E0E0E0',
                        borderTop: 'none',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        <MarkdownRenderer content={convertUrlsToMarkdown(duvida.textResposta)} />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
} 